import type { ModelAssetNode } from '../assets/model-asset-types'
import {
  isAssemblyPersistedDocument,
  validateAssemblyDocument,
} from '../assembly-document/assembly-document-operations'
import type {
  AssemblyDocument,
  AssemblyDocumentNode,
  AssemblyTransform,
} from '../assembly-document/assembly-document-types'

const IDENTITY_TRANSFORM: AssemblyTransform = [
  1, 0, 0, 0,
  0, 1, 0, 0,
  0, 0, 1, 0,
  0, 0, 0, 1,
]

/**
 * 判断未知 JSON 是否是普通对象。
 *
 * @param value `_assembly.json` 解析后的未知 JSON。
 * @returns 如果值是非数组对象则返回 true。
 */
function isJsonObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

/**
 * 判断未知 JSON 是否显式声明了装配文档 schemaVersion。
 *
 * @param value `_assembly.json` 解析后的未知 JSON。
 * @returns 如果对象自身携带 schemaVersion 字段则返回 true。
 */
function hasDeclaredSchemaVersion(value: unknown): value is { readonly schemaVersion: unknown } {
  return isJsonObject(value) && Object.prototype.hasOwnProperty.call(value, 'schemaVersion')
}

/**
 * 把节点局部矩阵收敛成装配文档可持久化的 4x4 矩阵。
 *
 * OpenProject 是当前装配层级与位姿的主来源；如果后端返回的 transform
 * 结构异常，则退回单位矩阵，避免把不可校验的数据写入工作草稿。
 *
 * @param transform OpenProject/GetCadNodeTree 映射后的节点局部矩阵。
 * @returns 可被 AssemblyDocument 校验器消费的 16 位矩阵。
 */
function toAssemblyTransform(transform: readonly number[]): AssemblyTransform {
  if (
    transform.length === 16
    && transform.every((item) => typeof item === 'number' && Number.isFinite(item))
  ) {
    return [...transform] as unknown as AssemblyTransform
  }

  return [...IDENTITY_TRANSFORM] as unknown as AssemblyTransform
}

/**
 * 从模型资产节点创建装配文档节点记录。
 *
 * @param node OpenProject/GetCadNodeTree 映射后的模型资产节点。
 * @returns 当前节点对应的装配草稿节点记录。
 */
function createAssemblyDocumentNode(node: ModelAssetNode): AssemblyDocumentNode {
  return {
    nodeKey: node.nodeKey,
    nodeType: node.nodeType,
    repositoryId: node.repositoryId,
    branchId: node.branchId,
    commitId: node.commitId,
    transform: toAssemblyTransform(node.localTransform),
  }
}

/**
 * 从 OpenProject 节点树合成 cad-core 内部的装配文档。
 *
 * 当前后端历史 `_assembly.json` 可能只是装配元数据而没有 schemaVersion。
 * 这类文件不能直接作为内部草稿消费，因此以 OpenProject 快照作为权威层级，
 * 生成首版可编辑的 AssemblyDocument，再交给统一校验器兜底。
 *
 * @param root 当前 `_assembly.json` 所属的装配根节点。
 * @returns 可用于 AssemblyDraftDocumentStore 的首版装配文档。
 */
function createAssemblyDocumentFromModelAssetNode(root: ModelAssetNode): AssemblyDocument {
  const nodesByKey: Record<string, AssemblyDocumentNode> = {}
  const childrenByNodeKey: Record<string, readonly string[]> = {}

  const visit = (node: ModelAssetNode): void => {
    nodesByKey[node.nodeKey] = createAssemblyDocumentNode(node)
    childrenByNodeKey[node.nodeKey] = node.children.map((child) => child.nodeKey)

    for (const child of node.children) {
      visit(child)
    }
  }

  visit(root)

  const document: AssemblyDocument = {
    schemaVersion: 1,
    rootNodeKey: root.nodeKey,
    nodesByKey,
    childrenByNodeKey,
  }

  validateAssemblyDocument(document)
  return document
}

/**
 * 归一化项目模型加载得到的 `_assembly.json`。
 *
 * - 已声明 schemaVersion 的文件必须严格符合 cad-core 内部 schema。
 * - 未声明 schemaVersion 的后端元数据按 OpenProject 节点树合成首版草稿。
 *
 * @param root 当前 `_assembly.json` 所属的装配根节点。
 * @param value `_assembly.json` 解析后的未知 JSON。
 * @returns 可被项目模型加载链路消费的装配文档。
 */
export function normalizeProjectModelAssemblyDocument(root: ModelAssetNode, value: unknown): AssemblyDocument {
  if (hasDeclaredSchemaVersion(value)) {
    const document = value as AssemblyDocument

    validateAssemblyDocument(document)
    return document
  }

  if (isAssemblyPersistedDocument(value)) {
    validateAssemblyDocument(value)
    return value
  }

  if (!isJsonObject(value)) {
    validateAssemblyDocument(value as AssemblyDocument)
    return value as AssemblyDocument
  }

  return createAssemblyDocumentFromModelAssetNode(root)
}
